import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "IdLivenessVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    id_liveness_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    customer_image: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        personal_information: false,
        facial_comparison_liveness: false,
        verification_results: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    customer_image_src() {
      if (!this.customer_image) return null;
      if (this.customer_image.startsWith("data:")) return this.customer_image;
      return `data:image/jpeg;base64,${this.customer_image}`;
    },
    safe_id_liveness_data() {
      return this.id_liveness_data || {};
    },

    // Report metadata
    report_reference() {
      return this.safe_id_liveness_data.CRef || `ID-LIV-${Date.now().toString().slice(-8)}`;
    },

    generated_date() {
      return new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    // Main result data
    result_data() {
      return this.safe_id_liveness_data || {};
    },

    // Personal Information
    first_name() {
      return this.result_data.FirstName || "N/A";
    },

    last_name() {
      return this.result_data.LastName || "N/A";
    },

    full_name() {
      return `${this.first_name} ${this.last_name}`;
    },

    id_number() {
      return this.result_data.IdNumber || "N/A";
    },

    marital_status() {
      return this.result_data.MaritalStatus || "N/A";
    },

    smart_card_issued() {
      return this.result_data.SmartCardIssued || false;
    },

    smart_card_status() {
      return this.smart_card_issued ? "Issued" : "Not Issued";
    },

    id_issue_date() {
      return this.result_data.IDIssueDate || "N/A";
    },

    formatted_id_issue_date() {
      return this.formatDate(this.id_issue_date);
    },

    id_sequence_number() {
      return this.result_data.IDSequenceNumber || "N/A";
    },

    birth_place_country() {
      return this.result_data.BirthPlaceCountry || "N/A";
    },

    birth_place_country_code() {
      return this.result_data.BirthPlaceCountryCode || "N/A";
    },

    birth_place() {
      return this.birth_place_country;
    },

    citizenship_description() {
      const country = this.birth_place_country;
      if (country === "SOUTH AFRICA") {
        return "a South African citizen";
      }
      return `a citizen of ${country}`;
    },

    // Status Indicators
    dead_indicator() {
      return this.result_data.DeadIndicator || false;
    },

    dead_indicator_text() {
      return this.dead_indicator ? "Yes" : "No";
    },

    date_of_death() {
      return this.result_data.DateOfDeath || null;
    },

    id_blocked() {
      return this.result_data.IDBlocked || false;
    },

    id_blocked_text() {
      return this.id_blocked ? "Yes" : "No";
    },

    on_hanis() {
      return this.result_data.OnHanis || false;
    },

    on_hanis_text() {
      return this.on_hanis ? "Yes" : "No";
    },

    on_npr() {
      return this.result_data.OnNPR || false;
    },

    on_npr_text() {
      return this.on_npr ? "Yes" : "No";
    },

    is_wallet_created() {
      return this.result_data.IsWalletCreated || false;
    },

    wallet_created_text() {
      return this.is_wallet_created ? "Yes" : "No";
    },

    // Verification Results
    first_name_result() {
      return this.result_data.FirstNameResult || "0";
    },

    last_name_result() {
      return this.result_data.LastNameResult || "0";
    },

    first_name_result_class() {
      const score = parseFloat(this.first_name_result);
      if (score >= 80) return "bg-success";
      if (score >= 50) return "bg-warning text-dark";
      return "bg-danger";
    },

    last_name_result_class() {
      const score = parseFloat(this.last_name_result);
      if (score >= 80) return "bg-success";
      if (score >= 50) return "bg-warning text-dark";
      return "bg-danger";
    },

    // Face Result
    face_result() {
      return this.result_data.FaceResult || {};
    },

    face_is_identical() {
      return this.face_result.isIdentical || false;
    },

    face_confidence() {
      return this.face_result.confidence || 0;
    },

    face_confidence_percentage() {
      return (this.face_confidence * 100).toFixed(1);
    },

    face_result_status() {
      return this.face_is_identical ? "Match" : "No Match";
    },

    face_result_text() {
      return `${this.face_confidence_percentage}% (${this.face_result_status})`;
    },

    face_result_class() {
      if (this.face_is_identical && this.face_confidence >= 0.7) {
        return "text-success";
      } else if (this.face_confidence >= 0.5) {
        return "text-warning";
      }
      return "text-danger";
    },

    // Liveness Result
    liveness_result() {
      return this.result_data.LivenessResult || {};
    },

    liveness_result_score() {
      return this.liveness_result.LivenessResultScore || 0;
    },

    liveness_result_probability() {
      return this.liveness_result.LivenessResultProbability || 0;
    },

    liveness_probability_percentage() {
      return (this.liveness_result_probability * 100).toFixed(1);
    },

    liveness_result_quality() {
      return this.liveness_result.LivenessResultQuality || 0;
    },

    liveness_quality_score() {
      return this.liveness_result_quality.toFixed(3);
    },

    liveness_quality_percentage() {
      return (this.liveness_result_quality * 100).toFixed(1);
    },

    liveness_result_class() {
      return this.liveness_result.LivenessResultClass || false;
    },

    liveness_pass_result() {
      return this.liveness_result.LivenessPassResult || false;
    },

    liveness_pass_text() {
      return this.liveness_pass_result ? "Passed" : "Failed";
    },

    liveness_pass_class() {
      return this.liveness_pass_result ? "bg-success" : "bg-danger";
    },

    liveness_message() {
      return this.liveness_result.LivenessMessage || "N/A";
    },

    liveness_result_text() {
      return this.liveness_pass_result ? "Live" : "Spoof";
    },

    liveness_status_text() {
      return this.liveness_result_text;
    },

    liveness_status_class() {
      return this.liveness_pass_result ? "text-success" : "text-danger";
    },

    // Overall Status
    overall_status() {
      return this.safe_id_liveness_data.Status ? this.safe_id_liveness_data.Status.toLowerCase() : "unknown";
    },

    overall_status_text() {
      return this.overall_status === "success" ? "Verified" : "Failed";
    },

    overall_status_class() {
      return this.overall_status === "success" ? "bg-success" : "bg-danger";
    },

    id_verification_text() {
      return this.overall_status_text;
    },

    id_verification_class() {
      return this.overall_status === "success" ? "text-success" : "text-danger";
    },

    // Images
    facial_image_available() {
      return this.result_data.FacialImageAvailable || false;
    },

    facial_image() {
      return this.result_data.FacialImage || null;
    },

    facial_image_src() {
      if (!this.facial_image || !this.facial_image_available) return null;
      if (this.facial_image.startsWith("data:")) return this.facial_image;
      return `data:image/jpeg;base64,${this.facial_image}`;
    },

    // Additional Information
    tracking_number() {
      return this.result_data.TrackingNumber || "N/A";
    },

    cached_result() {
      return this.result_data.CachedResult || false;
    },

    cache_date() {
      return this.result_data.CacheDate || null;
    },

    cref() {
      return this.result_data.CRef || "N/A";
    },

    message() {
      return this.result_data.Message || "N/A";
    },

    // Export validation
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },

    show_export_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("show");
    },

    close_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("hide");
    },

    async load_libraries() {
      if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
        await this.load_papa_parse();
      }
      if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
        await this.load_sheetjs();
      }
    },

    load_papa_parse() {
      return new Promise((resolve, reject) => {
        if (window.Papa) {
          this.libraries_loaded.papa_parse = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://unpkg.com/papaparse@5.3.2/papaparse.min.js";
        script.onload = () => {
          this.libraries_loaded.papa_parse = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    load_sheetjs() {
      return new Promise((resolve, reject) => {
        if (window.XLSX) {
          this.libraries_loaded.sheetjs = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js";
        script.onload = () => {
          this.libraries_loaded.sheetjs = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    prepare_personal_information_data() {
      return [
        {
          field: "First Name",
          value: this.first_name,
        },
        {
          field: "Last Name",
          value: this.last_name,
        },
        {
          field: "Full Name",
          value: this.full_name,
        },
        {
          field: "ID Number",
          value: this.id_number,
        },
        {
          field: "Marital Status",
          value: this.marital_status,
        },
        {
          field: "Birth Place",
          value: this.birth_place,
        },
        {
          field: "ID Issue Date",
          value: this.formatted_id_issue_date,
        },
        {
          field: "Smart Card Status",
          value: this.smart_card_status,
        },
        {
          field: "ID Sequence Number",
          value: this.id_sequence_number,
        },
      ];
    },

    prepare_facial_comparison_liveness_data() {
      return [
        {
          metric: "Face Match Status",
          value: this.face_result_status,
          score: this.face_confidence_percentage + "%",
        },
        {
          metric: "Face Confidence",
          value: this.face_confidence,
          score: this.face_confidence_percentage + "%",
        },
        {
          metric: "Liveness Status",
          value: this.liveness_status_text,
          score: this.liveness_pass_text,
        },
        {
          metric: "Liveness Score",
          value: this.liveness_result_score,
          score: this.liveness_quality_score,
        },
        {
          metric: "Liveness Probability",
          value: this.liveness_result_probability,
          score: this.liveness_probability_percentage + "%",
        },
        {
          metric: "Liveness Quality",
          value: this.liveness_result_quality,
          score: this.liveness_quality_percentage + "%",
        },
        {
          metric: "Liveness Message",
          value: this.liveness_message,
          score: "N/A",
        },
      ];
    },

    prepare_verification_results_data() {
      return [
        {
          check: "First Name Match",
          result: this.first_name_result + "%",
          status: this.first_name_result >= 80 ? "Pass" : "Fail",
        },
        {
          check: "Last Name Match",
          result: this.last_name_result + "%",
          status: this.last_name_result >= 80 ? "Pass" : "Fail",
        },
        {
          check: "Face Verification",
          result: this.face_result_status,
          status: this.face_is_identical ? "Pass" : "Fail",
        },
        {
          check: "Liveness Check",
          result: this.liveness_pass_text,
          status: this.liveness_pass_result ? "Pass" : "Fail",
        },
        {
          check: "Overall Verification",
          result: this.overall_status_text,
          status: this.overall_status === "success" ? "Pass" : "Fail",
        },
        {
          check: "Dead Indicator",
          result: this.dead_indicator_text,
          status: this.dead_indicator ? "Alert" : "Clear",
        },
        {
          check: "ID Blocked",
          result: this.id_blocked_text,
          status: this.id_blocked ? "Alert" : "Clear",
        },
        {
          check: "On HANIS",
          result: this.on_hanis_text,
          status: this.on_hanis ? "Verified" : "Not Found",
        },
        {
          check: "On NPR",
          result: this.on_npr_text,
          status: this.on_npr ? "Verified" : "Not Found",
        },
      ];
    },

    async export_data() {
      try {
        await this.load_libraries();

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        
        if (this.export_format === "json") {
          this.export_json(timestamp);
        } else if (this.export_format === "csv") {
          this.export_csv(timestamp);
        } else if (this.export_format === "excel") {
          this.export_excel(timestamp);
        }

        this.close_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },

    export_json(timestamp) {
      const data = {};

      if (this.export_options.personal_information) {
        data.personal_information = this.prepare_personal_information_data();
      }

      if (this.export_options.facial_comparison_liveness) {
        data.facial_comparison_liveness = this.prepare_facial_comparison_liveness_data();
      }

      if (this.export_options.verification_results) {
        data.verification_results = this.prepare_verification_results_data();
      }

      if (this.export_options.raw_json) {
        data.raw_data = this.safe_id_liveness_data;
      }

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `id-liveness-verification-${timestamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    export_csv(timestamp) {
      if (this.export_options.personal_information) {
        const data = this.prepare_personal_information_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `personal-information-${timestamp}.csv`);
      }

      if (this.export_options.facial_comparison_liveness) {
        const data = this.prepare_facial_comparison_liveness_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `facial-comparison-liveness-${timestamp}.csv`);
      }

      if (this.export_options.verification_results) {
        const data = this.prepare_verification_results_data();
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `verification-results-${timestamp}.csv`);
      }

      if (this.export_options.raw_json) {
        // Flatten the raw data for CSV export
        const flattened = this.flatten_object(this.safe_id_liveness_data);
        const data = Object.entries(flattened).map(([key, value]) => ({
          property: key,
          value: value,
        }));
        const csv = window.Papa.unparse(data);
        this.download_csv(csv, `raw-data-${timestamp}.csv`);
      }
    },

    export_excel(timestamp) {
      const workbook = window.XLSX.utils.book_new();

      if (this.export_options.personal_information) {
        const data = this.prepare_personal_information_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Personal Info");
      }

      if (this.export_options.facial_comparison_liveness) {
        const data = this.prepare_facial_comparison_liveness_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Facial & Liveness");
      }

      if (this.export_options.verification_results) {
        const data = this.prepare_verification_results_data();
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Verification Results");
      }

      if (this.export_options.raw_json) {
        const flattened = this.flatten_object(this.safe_id_liveness_data);
        const data = Object.entries(flattened).map(([key, value]) => ({
          property: key,
          value: value,
        }));
        const worksheet = window.XLSX.utils.json_to_sheet(data);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Raw Data");
      }

      window.XLSX.writeFile(workbook, `id-liveness-verification-${timestamp}.xlsx`);
    },

    download_csv(csvContent, filename) {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },    flatten_object(obj, prefix = "") {
      let flattened = {};
      for (let key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const newKey = prefix ? `${prefix}.${key}` : key;
          if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
            Object.assign(flattened, this.flatten_object(obj[key], newKey));
          } else {
            flattened[newKey] = obj[key];
          }
        }
      }
      return flattened;
    },
  },
};
